'use client';

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import i18n from '@/i18n/config';

import {
  DEFAULT_LANGUAGE_CODE,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from '@/constants/language.constants';

interface LanguageContextType {
  language: SupportedLanguage;
  setLanguage: (code: string) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = (): LanguageContextType => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};

/**
 * SSR-safe version: returns the default language instead of throwing
 * when called outside LanguageProvider (e.g. during prerendering).
 */
export const useSafeLanguage = (): LanguageContextType => {
  const ctx = useContext(LanguageContext);
  return (
    ctx ?? {
      language: SUPPORTED_LANGUAGES.find((l) => l.code === DEFAULT_LANGUAGE_CODE)!,
      setLanguage: () => {},
    }
  );
};

const STORAGE_KEY = 'app_language';

const getLanguageByCode = (code: string): SupportedLanguage =>
  SUPPORTED_LANGUAGES.find((l) => l.code === code) ??
  SUPPORTED_LANGUAGES.find((l) => l.code === DEFAULT_LANGUAGE_CODE)!;

/**
 * Detect the user's country via ipapi.co and return the appropriate
 * language code: 'en' for India, 'es' for everywhere else.
 * Falls back to DEFAULT_LANGUAGE_CODE if the request fails.
 */
async function detectLanguageByGeo(): Promise<string> {
  try {
    const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(4000) });
    if (!res.ok) return DEFAULT_LANGUAGE_CODE;
    const data = await res.json();
    // India → English, all other countries → Spanish
    return data.country_code === 'IN' ? 'en' : 'es';
  } catch {
    return DEFAULT_LANGUAGE_CODE;
  }
}

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguageState] = useState<SupportedLanguage>(() =>
    getLanguageByCode(DEFAULT_LANGUAGE_CODE),
  );

  // On mount: restore saved preference OR auto-detect by geo
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      // User already made an explicit choice — respect it
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLanguageState(getLanguageByCode(saved));
    } else {
      // First visit — detect by location and save the result
      detectLanguageByGeo().then((code) => {
        const lang = getLanguageByCode(code);

        setLanguageState(lang);
        localStorage.setItem(STORAGE_KEY, lang.code);
        i18n.changeLanguage(lang.code);
      });
    }
  }, []);

  // Sync i18n whenever the active language changes
  useEffect(() => {
    if (i18n.language !== language.code) {
      i18n.changeLanguage(language.code);
    }
  }, [language.code]);

  const setLanguage = useCallback((code: string) => {
    const lang = getLanguageByCode(code);
    setLanguageState(lang);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, lang.code);
      i18n.changeLanguage(lang.code);
    }
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
